import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Pause, Play, Plus, Trash2 } from 'lucide-react';
import {
  formatMinutes,
  type BoardSnapshot,
  type CardDetail,
  type RunningTimer,
  type TimeEntry,
} from '@kaba/shared';
import { api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { cardKey, useUpdateCard } from '../../lib/board';
import { toast } from '../../lib/toast';
import { Button, cx } from '../ui/ui';

const input =
  'min-h-11 w-full rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2.5 text-base outline-none focus:border-accent sm:min-h-10 sm:text-sm';

export function useRunningTimer() {
  return useQuery({ queryKey: ['timer'], queryFn: () => api.get<RunningTimer | null>('/api/me/timer'), staleTime: 30_000 });
}

export async function stopTimer(qc: ReturnType<typeof useQueryClient>) {
  await api.del('/api/timer');
  qc.setQueryData(['timer'], null);
}

/** Aufwand schätzen und Zeit erfassen (Timer oder von Hand); die Wiederholung steht seit 0.66 in SeriesSection */
export function TimeSection({
  card,
  snapshot,
  canEdit,
  part,
}: {
  card: CardDetail;
  snapshot: BoardSnapshot;
  canEdit: boolean;
  /** Nur Wiederholung (gehört zur Planung) oder nur Zeit (Aufwand) */
  part?: 'recurrence' | 'time';
}) {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const update = useUpdateCard(snapshot.board.id);
  const { data: running } = useRunningTimer();
  const { data: entries } = useQuery({
    queryKey: ['time', card.id],
    queryFn: () => api.get<TimeEntry[]>(`/api/cards/${card.id}/time`),
  });
  const runningHere = running?.cardId === card.id;
  const [, tick] = useState(0);
  useEffect(() => {
    if (!runningHere) return;
    const t = setInterval(() => tick((x) => x + 1), 30_000);
    return () => clearInterval(t);
  }, [runningHere]);

  const [manual, setManual] = useState(false);
  const [mins, setMins] = useState('');
  const [note, setNote] = useState('');
  const [estimate, setEstimate] = useState(card.estimateMinutes ? String(card.estimateMinutes / 60).replace('.', ',') : '');
  useEffect(() => setEstimate(card.estimateMinutes ? String(card.estimateMinutes / 60).replace('.', ',') : ''), [card.estimateMinutes]);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['time', card.id] });
    void qc.invalidateQueries({ queryKey: cardKey(card.id) });
  };

  const elapsed = runningHere && running ? Math.floor((Date.now() - new Date(running.startedAt).getTime()) / 60_000) : 0;
  const members = new Map(snapshot.members.map((m) => [m.id, m]));
  const pct = card.estimateMinutes ? Math.min(100, Math.round((card.spentMinutes / card.estimateMinutes) * 100)) : 0;
  const over = !!card.estimateMinutes && card.spentMinutes > card.estimateMinutes;


  return (
    <section className="flex flex-col gap-4">
      {/* ---------- Zeit ---------- */}
      {part !== 'recurrence' && (
      <div className="flex flex-col gap-2">
        {!part && <span className="text-xs font-semibold tracking-wide text-[var(--ink-soft)] uppercase">Zeit</span>}
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-xs text-[var(--ink-soft)]">Schätzung (Stunden)</span>
            <input
              className={cx(input, 'w-28')}
              inputMode="decimal"
              placeholder="z. B. 1,5"
              value={estimate}
              disabled={!canEdit}
              onChange={(e) => setEstimate(e.target.value)}
              onBlur={() => {
                const h = Number(estimate.replace(',', '.'));
                const m = estimate.trim() ? Math.round(h * 60) : null;
                if (estimate.trim() && !Number.isFinite(h)) return toast.error('Bitte eine Zahl eingeben, z. B. 1,5');
                if (m !== card.estimateMinutes) update.mutate({ id: card.id, patch: { estimateMinutes: m } });
              }}
            />
          </label>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-[var(--ink-soft)]">Erfasst</span>
            <span className={cx('min-h-10 text-sm leading-10 font-medium tabular-nums', over && 'text-rose-600')}>
              {formatMinutes(card.spentMinutes + (runningHere ? 0 : 0))}
              {card.estimateMinutes ? ` von ${formatMinutes(card.estimateMinutes)}` : ''}
            </span>
          </div>
          {canEdit && (
            <div className="ml-auto flex gap-2">
              {runningHere ? (
                <Button
                  variant="danger"
                  onClick={async () => {
                    await stopTimer(qc);
                    refresh();
                  }}
                >
                  <Pause size={16} /> Stopp ({formatMinutes(elapsed)})
                </Button>
              ) : (
                <Button
                  onClick={async () => {
                    try {
                      const r = await api.post<RunningTimer>(`/api/cards/${card.id}/timer`);
                      qc.setQueryData(['timer'], r);
                      if (running && running.cardId !== card.id) toast.info(`Timer bei „${running.cardTitle}“ wurde beendet.`);
                      refresh();
                    } catch (err) {
                      toast.error((err as Error).message);
                    }
                  }}
                >
                  <Play size={16} /> Timer starten
                </Button>
              )}
              <Button onClick={() => setManual((m) => !m)} aria-expanded={manual}>
                <Plus size={16} /> Zeit nachtragen
              </Button>
            </div>
          )}
        </div>
        {card.estimateMinutes ? (
          <div className="h-1.5 overflow-hidden rounded-full bg-black/5 dark:bg-white/10" aria-hidden>
            <div className={cx('h-full rounded-full', over ? 'bg-rose-500' : 'bg-accent')} style={{ width: `${pct}%` }} />
          </div>
        ) : null}

        {manual && (
          <form
            className="flex flex-wrap items-end gap-2 rounded-xl border border-[var(--line)] p-2"
            onSubmit={async (e) => {
              e.preventDefault();
              const raw = mins.trim().replace(',', '.');
              // „1:30“, „1,5“ (Stunden) oder „90“ (Minuten)
              let minutes = 0;
              if (raw.includes(':')) {
                const [h, m] = raw.split(':').map(Number);
                minutes = (h ?? 0) * 60 + (m ?? 0);
              } else {
                const n = Number(raw);
                minutes = raw.includes('.') || n <= 12 ? Math.round(n * 60) : Math.round(n);
              }
              if (!Number.isFinite(minutes) || minutes < 1) return toast.error('Bitte Dauer angeben, z. B. 1:30, 1,5 oder 90');
              try {
                await api.post(`/api/cards/${card.id}/time`, { minutes, note: note.trim() || null });
                setMins('');
                setNote('');
                setManual(false);
                refresh();
              } catch (err) {
                toast.error((err as Error).message);
              }
            }}
          >
            <label className="flex flex-col gap-1 text-xs">
              Dauer
              <input className={cx(input, 'w-28')} autoFocus placeholder="1:30" value={mins} onChange={(e) => setMins(e.target.value)} />
            </label>
            <label className="flex min-w-40 flex-1 flex-col gap-1 text-xs">
              Notiz (optional)
              <input className={input} value={note} onChange={(e) => setNote(e.target.value)} />
            </label>
            <Button type="submit" variant="primary">
              Eintragen
            </Button>
          </form>
        )}

        {!!entries?.length && (
          <ul className="flex flex-col divide-y divide-[var(--line)] text-sm">
            {entries.slice(0, 8).map((e) => (
              <li key={e.id} className="flex min-h-10 items-center gap-2">
                <span className="w-24 shrink-0 text-xs text-[var(--ink-soft)]">{new Date(e.startedAt).toLocaleDateString('de-DE')}</span>
                <span className="w-20 shrink-0 font-medium tabular-nums">{e.endedAt ? formatMinutes(e.minutes) : 'läuft …'}</span>
                <span className="min-w-0 flex-1 truncate text-[var(--ink-soft)]">
                  {members.get(e.userId)?.name}
                  {e.note ? ` · ${e.note}` : ''}
                </span>
                {canEdit && e.endedAt && (e.userId === me?.id || snapshot.board.role === 'owner') && (
                  <button
                    aria-label="Eintrag löschen"
                    className="inline-flex size-9 items-center justify-center rounded-md text-[var(--ink-soft)] hover:text-rose-600"
                    onClick={async () => {
                      await api.del(`/api/time/${e.id}`);
                      refresh();
                    }}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      )}
    </section>
  );
}
